import React, { FC, useMemo } from 'react';
import { FlexCol, Loader, GridDynamic, FlexDynamic, FlexRow, TextDynamic } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { WindowWidthContext } from '@minecraft.utils';
import Icon from '@minecraft.icon';
import { ALL_BRANDS } from '@minecraft.molecules';
import { WalletLoader } from './WalletLoader';
import { PaymentLoader } from './PaymentLoader';
import { CheckoutProps } from './types';
import { Purchase } from '../types';
import { AddPayment } from './AddPayment';
import { CheckoutHead } from './CheckoutHead';
import { InfiniteBrandSroller } from './InfiniteBrandSroller';
import { PlatformUpsell } from '../PlatformUpsell';

export const CheckoutV2: FC<CheckoutProps> = ({
  onNext,
  currentPurchase,
  onCompletePurchase,
  currencyCode,
  onApplyCoupon,
  couponLoading,
  applyCouponError,
  onRemoveCoupon,
  completePurchaseLoading,
  completePurchaseError,
  wallet,
  walletLoading,
  selectedPaymentOption,
  onSelectPaymentOption,
  addPaymentError,
  isAnnual,
  plans,
  setIsAnnual,
  selectedPlan,
  setSelectedPlan,
  countryCode,
  onAddPaymentOption,
  onUpdatePaymentOption,
  setShowAddPaymentMethod = () => null,
  showAddPaymentMethod,
  addPaymentLoading,
  threeDActionContext,
  onThreeDActionError,
  paywallLocation,
  enableAnnualRenewal,
  closeBillingPopup,
  onPaymentCompleteRedirect,
  isAnnualToggleLocked,
  showApplePayButton,
  onClickApplePay,
  requestRecurlyTokenAndValidate,
  handlePaymentPriorityChange,
  paymentMethodFormRef,
  paymentMethodFormikRef,
  isModal,
}) => {
  const { t } = useTranslation();
  const isMobile = WindowWidthContext.useIsMobile();
  const disableTrial = Boolean((currentPurchase as Purchase)?.coupon?.code);
  const upsellItems = useMemo(
    () => [
      {
        icon: 'social-instagram',
        title: t('common:paywall.checkout.upsellSocialLinks'),
      },
      {
        icon: 'role-tracker',
        title: t('common:paywall.checkout.upsellRoleTracker'),
      },
      {
        icon: 'thrive',
        title: t('common:paywall.checkout.upsellThrive'),
      },
      {
        icon: 'talent-scout',
        title: t('common:paywall.checkout.upsellTalentScout'),
      },
    ],
    [t]
  );
  return (
    <GridDynamic
      data-testid="paywall-checkout-wrapper"
      w="full"
      columns="1fr"
      mdProps={{
        columns: '1fr 1fr',
        gap: '8',
      }}
      gap="0"
    >
      <FlexDynamic gap="2" order={2} my="8" mx="2" mdProps={{ ml: '6', order: 1 }}>
        <FlexDynamic w="full" gap="8" mt="0">
          {isMobile && (
            <FlexCol>
              <InfiniteBrandSroller />
            </FlexCol>
          )}
          <FlexCol w="fit">
            <CheckoutHead
              isAnnual={isAnnual}
              setIsAnnual={setIsAnnual}
              plans={plans}
              countryCode={countryCode}
              currencyCode={currencyCode}
              isToggleLocked={isAnnualToggleLocked}
            />
          </FlexCol>
          <Loader loading={completePurchaseLoading} LoaderComponent={<PaymentLoader />}>
            <FlexCol gap="4" w="full">
              <Loader loading={walletLoading} LoaderComponent={<WalletLoader />}>
                <AddPayment
                  setIsAnnual={setIsAnnual}
                  isAnnual={isAnnual}
                  onCancel={() => setShowAddPaymentMethod(false)}
                  addPaymentError={addPaymentError}
                  addPaymentLoading={addPaymentLoading}
                  allowCancel={!!wallet?.length}
                  onAddPaymentOption={onAddPaymentOption}
                  onUpdatePaymentOption={onUpdatePaymentOption}
                  threeDActionContext={threeDActionContext}
                  onThreeDActionError={onThreeDActionError}
                  currencyCode={currencyCode}
                  countryCode={countryCode}
                  paywallLocation={paywallLocation}
                  onCompletePurchase={onCompletePurchase}
                  selectedPlan={selectedPlan}
                  currentPurchase={currentPurchase as Purchase}
                  completePurchaseError={completePurchaseError}
                  completePurchaseLoading={completePurchaseLoading}
                  selectedPaymentOption={selectedPaymentOption}
                  wallet={wallet}
                  onSelectPaymentOption={onSelectPaymentOption}
                  onNext={onNext}
                  plans={plans}
                  setSelectedPlan={setSelectedPlan}
                  onPaymentCompleteRedirect={onPaymentCompleteRedirect}
                  disableTrial={disableTrial}
                  onApplyCoupon={onApplyCoupon}
                  couponLoading={couponLoading}
                  applyCouponError={applyCouponError}
                  onRemoveCoupon={onRemoveCoupon}
                  showApplePayButton={showApplePayButton}
                  onClickApplePay={onClickApplePay}
                  requestRecurlyTokenAndValidate={requestRecurlyTokenAndValidate}
                  handlePaymentPriorityChange={handlePaymentPriorityChange}
                  paymentMethodFormRef={paymentMethodFormRef}
                  paymentMethodFormikRef={paymentMethodFormikRef}
                  showAddPaymentMethod={showAddPaymentMethod}
                  setShowAddPaymentMethod={setShowAddPaymentMethod}
                  closeBillingPopup={closeBillingPopup}
                  enableAnnualRenewal={enableAnnualRenewal}
                  isModal={isModal}
                />
              </Loader>
            </FlexCol>
          </Loader>
        </FlexDynamic>
      </FlexDynamic>
      <FlexDynamic order={1} gap="2" mdProps={{ order: 2 }}>
        <PlatformUpsell
          title={t('common:paywall.checkout.upsellTitle')}
          hideInMobile={false}
          hideTestimonial
          showBrands={ALL_BRANDS.filter((brand) => brand !== 'toyota')}
        >
          <FlexCol w="full" gap="6" className="cn_atom_pb-6">
            {upsellItems.map(({ icon, title }) => (
              <FlexRow w="full" justifyContent="flex-start" gap="3" alignItems="center" key={title} wrap="nowrap">
                <Icon name={icon} themeColor="inverted" />
                <TextDynamic
                  fontWeight="semibold"
                  color="inverted"
                  align="left"
                  fontSize="bodyRegular"
                  mdProps={{ fontSize: 'bodyLarge' }}
                >
                  {title}
                </TextDynamic>
              </FlexRow>
            ))}
          </FlexCol>
        </PlatformUpsell>
      </FlexDynamic>
    </GridDynamic>
  );
};
